import React from 'react';

export const LocalHiderPrivacyPolicy = () => {
  return (
    <main className="privacy-policy-page">
      <div className="privacy-policy-container">
        <h1>Privacy Policy - Local Hider</h1>
        <p className="privacy-policy-updated">Last updated: August 2026</p>

        <p>
          Local Hider ("we", "our", or "the app") is a real-world, GPS-based
          hide-and-seek game developed by Mindware. This Privacy Policy
          explains what information we collect when you use Local Hider, how
          we use it, and the choices you have. Because Local Hider is built
          around real-time location sharing between players, please read the
          section on Location Data carefully before you play.
        </p>

        <h2>1. Information We Collect</h2>
        <p>We collect the following categories of information:</p>
        <ul>
          <li>
            <strong>Account &amp; profile information:</strong> your display
            name or nickname, an avatar (which may be generated from your
            initials), and, if you create an account, your email address.
          </li>
          <li>
            <strong>Precise location data:</strong> your device's GPS
            coordinates while a match is active, so the app can place you on
            the match map, calculate distances, and answer clue-card
            questions such as Radar, Thermometer, or Distance to boundary.
          </li>
          <li>
            <strong>Match &amp; gameplay data:</strong> game codes and QR
            codes you create or join, the play area boundary and landmarks
            you draw, your role in a match (Hider or Seeker), clue cards and
            curses used, match duration, and stats such as hidden time and
            distance covered.
          </li>
          <li>
            <strong>Device &amp; technical data:</strong> device model,
            operating system and version, app version, language settings,
            and diagnostic or crash logs.
          </li>
          <li>
            <strong>Communications:</strong> messages you send us through
            support channels, including the content of your request and the
            email address you used to contact us.
          </li>
        </ul>

        <h2>2. Location Data — How It's Used</h2>
        <p>
          Location is central to how Local Hider works, so we want to be
          specific about it:
        </p>
        <ul>
          <li>
            We only access your precise location while you are actively
            participating in a match. We do not track your location in the
            background or when the app is closed.
          </li>
          <li>
            During a match, your location (or distance/temperature-style
            clues derived from it) is shared only with the other players in
            that same match — never with players outside your match, and
            never with the public.
          </li>
          <li>
            Location data is used strictly for gameplay: placing you on the
            match map, enforcing the play area boundary, and answering clue
            cards. We do not use your location for advertising, and we do
            not sell it.
          </li>
          <li>
            You can revoke location permission at any time from your device
            settings. Because location is required for core gameplay, doing
            so will prevent you from creating or joining matches.
          </li>
        </ul>

        <h2>3. How We Use Your Information</h2>
        <p>We use the information described above to:</p>
        <ul>
          <li>Run matches: connect players, draw the map, and resolve clue-card questions</li>
          <li>Maintain your match history, stats, and leaderboards</li>
          <li>Keep the app secure and prevent abuse or cheating</li>
          <li>Diagnose crashes and improve app performance</li>
          <li>Respond to support requests</li>
        </ul>
        <p>Local Hider does not sell your personal information.</p>

        <h2>4. How We Share Information</h2>
        <p>We do not sell or rent your information. We share it only with:</p>
        <ul>
          <li>
            Other players in your active match, to the extent necessary for
            gameplay (for example, your approximate position or the answer
            to a clue card)
          </li>
          <li>
            Service providers who host our infrastructure, send push
            notifications, or provide crash reporting and analytics on our
            behalf, under confidentiality obligations
          </li>
          <li>
            Authorities, if required by law or to protect the rights,
            safety, or property of Mindware, our players, or the public
          </li>
        </ul>

        <h2>5. Data Retention</h2>
        <p>
          We keep match history and stats for as long as your account is
          active, so you can review past games. Precise location points
          collected during a match are retained only as long as needed to
          resolve that match and are not kept indefinitely. You can request
          deletion of your account and associated data at any time (see
          Section 7).
        </p>

        <h2>6. Your Choices &amp; Rights</h2>
        <ul>
          <li>You can edit or delete your profile information from within the app.</li>
          <li>You can revoke location or notification permissions from your device settings.</li>
          <li>You can request a copy of your data, or request that we delete your account and associated data, by contacting us (Section 9).</li>
        </ul>

        <h2>7. Children's Privacy</h2>
        <p>
          Local Hider is not directed at children under 13, and we do not
          knowingly collect personal information from children under 13. If
          you believe a child has provided us with personal information,
          please contact us so we can remove it.
        </p>

        <h2>8. Security</h2>
        <p>
          We apply reasonable technical and organizational measures to
          protect your information, including encryption in transit.
          However, no method of transmission or storage is completely
          secure, and we cannot guarantee absolute security.
        </p>

        <h2>9. Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. Any changes
          will be reflected in the "Last updated" date above, and material
          changes will be communicated within the app where possible.
        </p>

        <h2>10. Contact Us</h2>
        <p>
          If you have questions about this Privacy Policy or how your data
          is handled, contact us at:
        </p>
        <p>
          Email:{' '}
          <a href="mailto:support_localhider@mindware.com.mx" className="privacy-policy-email">
            support_localhider@mindware.com.mx
          </a>
        </p>
        <p>
          Support page:{' '}
          <a href="https://mindware.com.mx/support" className="privacy-policy-email" target="_blank" rel="noopener noreferrer">
            mindware.com.mx/support
          </a>
        </p>
      </div>
    </main>
  );
};
